// La retouche du CV maître sur la fiche, avec l'éditeur de l'extension.
//
// Le CV affiché jusque-là n'était qu'un rendu : un titre mal lu par l'IA ne se
// corrigeait qu'en redéposant un PDF, donc en relançant l'IA. L'éditeur
// travaille sur un BROUILLON et n'écrit qu'au clic sur « Enregistrer » : le
// serveur revectorise le profil à chaque écriture, ce qu'on ne veut pas à
// chaque frappe.

import { useEffect, useState } from 'react'
import { Check, X } from 'lucide-react'
import { ProfileEditor, PROFILE_EDITOR_LABELS } from '@ai-job-fit/ui'
import { normalizeProfile, type ResumeProfile } from '@ai-job-fit/engine/schema'
import { post } from '../lib/api'
import { useLang } from '../lib/lang'
import { useT } from '../lib/i18n'
import { useToast } from './common'

export function EditeurCv({ consultantId, profile, onFini }: {
  consultantId: number
  profile: unknown
  /** `true` quand le CV a été enregistré (la fiche doit se relire). */
  onFini: (enregistre: boolean) => void
}) {
  const t = useT()
  const lang = useLang()
  const toast = useToast()
  const [brouillon, setBrouillon] = useState<ResumeProfile>(() => normalizeProfile(profile))
  const [modifie, setModifie] = useState(false)
  const [envoi, setEnvoi] = useState(false)

  // Fermer l'onglet sur une retouche non enregistrée la perdrait sans un mot.
  useEffect(() => {
    if (!modifie) return
    const retenir = (e: BeforeUnloadEvent) => { e.preventDefault() }
    window.addEventListener('beforeunload', retenir)
    return () => window.removeEventListener('beforeunload', retenir)
  }, [modifie])

  const enregistrer = async () => {
    setEnvoi(true)
    try {
      await post(`/api/consultants/${consultantId}/profile`, { profile: brouillon })
      toast(t('consultant.cvSaved'))
      onFini(true)
    } catch (e) {
      // Le refus nomme sa cause (expérience sans intitulé) : l'afficher tel
      // quel, et garder le brouillon pour qu'on puisse corriger.
      toast((e as Error).message, 'err')
    } finally {
      setEnvoi(false)
    }
  }

  const annuler = () => {
    if (modifie && !window.confirm(t('consultant.cvDiscard'))) return
    onFini(false)
  }

  const boutons = (
    <div className="editeur-cv-actions">
      <button className="btn-secondary" onClick={annuler} disabled={envoi}>
        <X size={15} />{t('consultant.cvCancel')}
      </button>
      <button className="btn-primary" onClick={enregistrer} disabled={envoi || !modifie}>
        <Check size={15} />{envoi ? t('consultant.cvSaving') : t('consultant.cvSave')}
      </button>
    </div>
  )

  return (
    <div className="editeur-cv">
      {boutons}
      <ProfileEditor
        profile={brouillon}
        onChange={(p) => { setBrouillon(p); setModifie(true) }}
        labels={PROFILE_EDITOR_LABELS[lang]}
      />
      {boutons}
    </div>
  )
}
